import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import axios from "axios";
import { FaSearch, FaTimes } from "react-icons/fa"; // Import icons
import { debounce } from "lodash"; // Add lodash for debouncing

const localizer = momentLocalizer(moment);

// Map for translating English day names to Polish
const dayTranslationMap = {
  niedziela: "Niedziela",
  poniedziałek: "Poniedziałek",
  wtorek: "Wtorek",
  środa: "Środa",
  czwartek: "Czwartek",
  piątek: "Piątek",
  sobota: "Sobota",
};

const TrainerCalendar = () => {
  const [isBreak, setIsBreak] = useState(false);
  const token = localStorage.getItem("token"); // Pobierz token z localStorage

  const [breakNote, setBreakNote] = useState("");
  const [karnetDetails, setKarnetDetails] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [currentView, setCurrentView] = useState(Views.DAY);
  const [modalData, setModalData] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [karnety, setKarnety] = useState([]);
  const [selectedKarnet, setSelectedKarnet] = useState("");
  const [breaks, setBreaks] = useState([]);
  const [firstVisits, setFirstVisits] = useState([]);
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false); // Track if user data is loaded
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const trainingTypeColors = {
    "Solo EMS": "#FFA500", // Jasny pomarańczowy
    "Para EMS": "#FF8C00", // Pomarańczowy
    "Para Personalny": "#DDA0DD", // Jasny fioletowy
    "Solo Personalny": "#FF69B4", // Ciemny fioletowy
    Hybryda: "#006400", // Ciemny zielony
  };

  const Star = ({ filled }) => (
    <span style={{ color: filled ? "gold" : "lightgray", fontSize: "1.2em" }}>
      ★
    </span>
  );

  // Separated fetch functions for calendar data
  const fetchCalendarData = useCallback(async () => {
    try {
      const [
        trainersResponse,
        treningsResponse,
        breaksResponse,
        firstVisitsResponse,
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/trenerzy/getTrenerzy`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${BACKEND_URL}/api/treningi/getTreningi`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${BACKEND_URL}/api/breaks/getAllBreaks`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${BACKEND_URL}/api/treningi/getFirstVisits`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      setTrainers(trainersResponse.data);

      // Filter out "anulowany" events
      const validTrainings = treningsResponse.data.filter(
        (event) => event.status !== "anulowany"
      );

      const formattedEvents = validTrainings.map((event) => {
        const karnet = event.karnet_id; // Store karnet_id in a variable for easier access

        const paymentsDueCount = karnet
          ? karnet.completed_trainings === 0
            ? 0
            : Math.max(
                0,
                karnet.przypomnienie_platnosc -
                  (karnet.completed_trainings % karnet.przypomnienie_platnosc) -
                  2
              )
          : 0;
        let czyWymaganaPlatnosc = false;
        let expectedInstallments = 0;
        let czyWiecej = false;
        let totalPayments = 0;
        if (karnet) {
          const reminderInterval = karnet.przypomnienie_platnosc;
          const totalInstallments = karnet.ilosc_rat;
          const completedTrainings = karnet.completed_trainings;

          expectedInstallments = Math.min(
            Math.floor((completedTrainings + 2) / reminderInterval) + 1,
            Math.ceil(karnet.cena / karnet.cena_za_rate)
          );

          totalPayments = Math.ceil(karnet.cena / karnet.cena_za_rate);

          // Jeśli oczekiwana liczba rat jest równa następnej racie do zapłaty
          if (totalPayments - expectedInstallments < totalInstallments) {
            czyWymaganaPlatnosc = true;
          }
          if (totalPayments - expectedInstallments >= totalInstallments) {
            czyWiecej = true;
          }
        }
        const remainingTrainings = karnet ? karnet.ilosc_wejsc : 0;

        return {
          title: `${event.uzytkownicy.imie} ${event.uzytkownicy.nazwisko}`,
          start: new Date(event.godzina_start),
          end: new Date(event.godzina_koniec),
          resourceId: event.trener_id._id,
          treningId: event._id,
          typ_treningu: event.typ_treningu,
          note: event.uzytkownicy.notatka || "",
          karnet_id: karnet,
          completed_trainings: karnet ? karnet.completed_trainings : 0,
          ilosc_rat: karnet ? karnet.ilosc_rat : null,
          cena_za_rate: karnet ? karnet.cena_za_rate : null,
          platnosc_rodzaj: karnet ? karnet.platnosc_rodzaj : null,
          karnet_oplacone: karnet ? karnet.karnet_oplacone : null,
          paymentsDueCount,
          status: karnet ? karnet.status : null,
          czyWymaganaPlatnosc,
          czyWiecej,
          totalPayments,
          expectedInstallments,
          remainingTrainings: karnet ? karnet.ilosc_wejsc : 0,
        };
      });

      const formattedBreaks = breaksResponse.data.map((breakItem) => ({
        title: "Przerwa",
        start: new Date(breakItem.start),
        end: new Date(breakItem.end),
        resourceId: breakItem.trainerId._id,
        note: breakItem.note || "",
      }));

      const formattedFirstVisits = firstVisitsResponse.data.map((visit) => ({
        title: `Próbny trening: ${visit.firstName} ${visit.lastName}`,
        start: new Date(visit.godzina_start),
        end: new Date(visit.godzina_koniec),
        resourceId: visit.trener_id,
        typ_treningu: visit.typ_treningu,
        isFirstVisit: true,
        visitData: visit,
      }));

      setBreaks(formattedBreaks);
      const allEvents = [...formattedEvents, ...formattedFirstVisits];
      const unavailableEvents = trainersResponse.data.flatMap((trainer) =>
        getUnavailableHours(trainer._id, currentDate)
      );

      setEvents([...allEvents, ...unavailableEvents]);
      setFirstVisits(firstVisitsResponse);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      alert(
        "Wystąpił błąd podczas ładowania danych. Spróbuj odświeżyć stronę."
      );
    }
  }, [currentDate, token]);

  // Function to load user data only when needed
  const loadUserData = useCallback(async () => {
    if (isUserDataLoaded) return; // Only fetch if not already loaded

    try {
      const usersResponse = await axios.get(
        `${BACKEND_URL}/api/user/getAllUsers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUsers(usersResponse.data);
      setFilteredUsers(usersResponse.data);
      setIsUserDataLoaded(true);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [token, isUserDataLoaded]);

  // Create debounced version of fetchCalendarData
  const debouncedFetchCalendarData = useMemo(
    () => debounce(fetchCalendarData, 300),
    [fetchCalendarData]
  );

  const getUnavailableHours = useCallback(
    (trainerId, selectedDate) => {
      const trainer = trainers.find((t) => t._id === trainerId);
      if (!trainer || !trainer.godziny_pracy) return [];

      const unavailableHours = [];

      // Get the day index from the selected date
      const selectedDayIndex = moment(selectedDate).day(); // Get the index of the selected day (0-6)
      const selectedDayName = moment(selectedDate).format("dddd"); // Get the name of the selected day in English
      const selectedDayNamePolish = dayTranslationMap[selectedDayName]; // Translate to Polish
      const selectedDateFormatted = moment(selectedDate).format("YYYY-MM-DD");

      const exceptionalHours = trainer.wyjatkowe_godziny_pracy?.find(
        (ex) => moment(ex.data).format("YYYY-MM-DD") === selectedDateFormatted
      );

      if (exceptionalHours) {
        // Jeśli są wyjątkowe godziny, blokujemy czas przed i po nich
        unavailableHours.push({
          title: "Niedostępny",
          start: moment(selectedDate).startOf("day").toDate(),
          end: moment(selectedDate)
            .hour(exceptionalHours.godzina_start.split(":")[0])
            .minute(exceptionalHours.godzina_start.split(":")[1])
            .toDate(),
          resourceId: trainerId,
          isUnavailable: true,
        });

        unavailableHours.push({
          title: "Niedostępny",
          start: moment(selectedDate)
            .hour(exceptionalHours.godzina_koniec.split(":")[0])
            .minute(exceptionalHours.godzina_koniec.split(":")[1])
            .toDate(),
          end: moment(selectedDate).endOf("day").toDate(),
          resourceId: trainerId,
          isUnavailable: true,
        });

        return unavailableHours; // **Jeśli są wyjątkowe godziny, kończymy tutaj**
      }
      trainer.godziny_pracy.forEach(
        ({ dzien, godzina_start, godzina_koniec }) => {
          // Check if the day matches the selected day
          if (dzien === selectedDayNamePolish) {
            // Create a moment object for the selected date
            const dayMoment = moment(selectedDate); // Set to the specific selected date

            // Block from 00:00 to godzina_start
            const startOfDay = dayMoment.clone().hour(0).minute(0).toDate();
            const endOfWorkingStart = dayMoment
              .clone()
              .hour(godzina_start.split(":")[0])
              .minute(godzina_start.split(":")[1])
              .toDate();

            unavailableHours.push({
              title: "Niedostępny",
              start: startOfDay,
              end: endOfWorkingStart,
              resourceId: trainerId,
              isUnavailable: true,
            });

            // Block from godzina_koniec to 24:00
            const startOfWorkingEnd = dayMoment
              .clone()
              .hour(godzina_koniec.split(":")[0])
              .minute(godzina_koniec.split(":")[1])
              .toDate();
            const endOfDay = dayMoment.clone().hour(23).minute(59).toDate(); // 24:00 is represented as 23:59

            unavailableHours.push({
              title: "Niedostępny",
              start: startOfWorkingEnd,
              end: endOfDay,
              resourceId: trainerId,
              isUnavailable: true,
            });
          }
        }
      );
      return unavailableHours;
    },
    [trainers]
  );

  // Only fetch calendar data on initial load and date change
  useEffect(() => {
    debouncedFetchCalendarData();
    return () => debouncedFetchCalendarData.cancel(); // Cancel debounce on unmount
  }, [debouncedFetchCalendarData]);

  // Filter users based on search term
  useEffect(() => {
    // Only filter if users are already loaded
    if (isUserDataLoaded) {
      const filtered = users.filter(
        (user) =>
          user.imie.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.nazwisko.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users, isUserDataLoaded]);

  const handleSelectSlot = useCallback(
    ({ start, end, resourceId }) => {
      if (currentView !== Views.DAY) {
        setCurrentDate(start);
        setCurrentView(Views.DAY);
        return;
      }

      // Load user data if needed when selecting a slot
      if (modalData === null) {
        loadUserData();
      }

      setModalData({ start, end, resourceId });
    },
    [currentView, modalData, loadUserData]
  );

  const handleSelectEvent = useCallback((event) => {
    if (event.title === "Przerwa") {
      alert(`Notatka: ${event.note}`);
    } else if (event.isFirstVisit) {
      setSelectedEvent({
        ...event,
        isFirstVisit: true,
        visitData: event.visitData,
      });
    } else if (event.isUnavailable) {
      // Do nothing for unavailable slots
    } else {
      setSelectedEvent(event);
    }
  }, []);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-center text-3xl font-extrabold mb-6 text-indigo- 600">
        Kalendarz Treningowy
      </h1>

      <Calendar
        localizer={localizer}
        events={[...events, ...breaks]}
        date={currentDate}
        startAccessor="start"
        endAccessor="end"
        view={currentView}
        views={[Views.DAY, Views.MONTH]}
        onNavigate={(date) => {
          setCurrentDate(date);
        }}
        step={30}
        timeslots={1}
        selectable
        resources={trainers.map((trainer) => ({
          id: trainer._id,
          name: `${trainer.imie} ${trainer.nazwisko}`,
        }))}
        resourceIdAccessor="id"
        resourceTitleAccessor="name"
        onSelectSlot={handleSelectSlot}
        onView={setCurrentView}
        style={{ height: 700 }}
        onSelectEvent={handleSelectEvent}
        culture="pl"
        formats={{
          timeGutterFormat: "HH:mm",
          eventTimeRangeFormat: ({ start, end }) =>
            `${moment(start).format("HH:mm")} - ${moment(end).format("HH:mm")}`,
        }}
        eventPropGetter={(event) => {
          let backgroundColor;
          if (event.isUnavailable) {
            backgroundColor = "#808080"; // Red for unavailable hours
          } else if (event.title === "Przerwa") {
            backgroundColor = "#FFD700";
          } else if (event.isFirstVisit) {
            if (
              event.typ_treningu ===
              "Konsultacja + trening personalny EMS + drenaż limfatyczny za 69 zł"
            ) {
              backgroundColor = "#FF1493"; // Różowy
            } else if (
              event.typ_treningu ===
              "Konsultacja + trening personalny siłowy (bez kombinezonu EMS) za 69 zł"
            ) {
              backgroundColor = "#1E90FF"; // Niebieski
            } else if (
              event.typ_treningu ===
              "Pierwsza wizyta Program Hybrydowy (EMS + siłowy) za 89 zł"
            ) {
              backgroundColor = "#32CD32"; // Zielony
            } else {
              backgroundColor = "#9932CC"; // Fioletowy dla innych pierwszych wizyt
            }
          } else {
            backgroundColor =
              trainingTypeColors[event.typ_treningu] || "#4682b4";
          }

          return {
            style: {
              backgroundColor:
                event.karnet_id &&
                event.platnosc_rodzaj == "rata" &&
                (event.paymentsDueCount === 0 || event.czyWymaganaPlatnosc) &&
                event.ilosc_rat > 0 &&
                !event.czyWiecej
                  ? "rgb(239, 68, 68)" // red-500
                  : backgroundColor,
              color: "white",
              borderRadius: "5px",
              padding: "10px",
              margin: "5px",
              transition: "background-color 0.3s",
            },
          };
        }}
        messages={{
          today: "Dziś",
          previous: "Poprzedni",
          next: "Następny",
          month: "Miesiąc",
          week: "Tydzień",
          day: "Dzień",
          agenda: "Agenda",
          date: "Data",
          time: "Czas",
          event: "Wydarzenie",
          noEventsInRange: "Brak wydarzeń w tym okresie",
        }}
      />

      {selectedEvent && selectedEvent.isFirstVisit && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-bold mb-4">
              Szczegóły Próbnego Treningu
            </h2>
            <p className="mb-2">
              Imię i Nazwisko: {selectedEvent.visitData.firstName}{" "}
              {selectedEvent.visitData.lastName}
            </p>
            <p className="mb-2">Telefon: {selectedEvent.visitData.phone}</p>
            <p className="mb-2">Email: {selectedEvent.visitData.email}</p>
            <p className="mb-2">
              Typ Treningu: {selectedEvent.visitData.typ_treningu}
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-lg"
                onClick={() => setSelectedEvent(null)}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedEvent && !selectedEvent.isFirstVisit && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-xl transform scale-100 transition-transform duration-300">
            <h2 className="text-xl font-semibold mb-4">Szczegóły Treningu</h2>
            <p className="mb-2">
              <strong>Użytkownik:</strong> {selectedEvent.title}
            </p>
            <p className="mb-2">
              <strong>Typ Treningu:</strong> {selectedEvent.typ_treningu}
            </p>
            <p className="mb-2">
              <strong>Notatka:</strong> {selectedEvent.note}
            </p>
            <p className="mb-2">
              <strong>Karnet Opłacone:</strong> {selectedEvent.karnet_oplacone}{" "}
              PLN
            </p>
            <p className="mb-2">
              <strong>Płatność za:</strong> {selectedEvent.paymentsDueCount}{" "}
              Trening
            </p>
            <p className="mb-2">
              <strong>Pozostało:</strong> {selectedEvent.remainingTrainings}{" "}
              Trening do konca karnetu
            </p>

            {selectedEvent.platnosc_rodzaj === "rata" ? (
              <>
                <p className="mb-2">
                  <strong>Ilosć Rat:</strong> {selectedEvent.ilosc_rat}
                </p>
                <p className="mb-2">
                  <strong>Cena za Ratę:</strong> {selectedEvent.cena_za_rate}{" "}
                  PLN
                </p>
              </>
            ) : (
              <p className="mb-2">
                <strong>Całość:</strong> {selectedEvent.karnet_id.cena} PLN
              </p>
            )}
            {selectedEvent.karnet_id && (
              <div className="mb-4">
                <strong>Gwiazdy: </strong>
                {Array.from(
                  { length: selectedEvent.karnet_id.max_gwiazdek },
                  (_, index) => (
                    <Star
                      key={index}
                      filled={index < selectedEvent.karnet_id.ilosc_gwiazdek}
                    />
                  )
                )}
              </div>
            )}

            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded-lg flex items-center transition"
                onClick={() => {
                  setSelectedEvent(null);
                  setKarnetDetails(null);
                }}
              >
                <FaTimes className="mr-2" /> Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerCalendar;
